import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { buildNicSummaryLines, buildNicVendorDeviceSummary, formatNicHardwareSummary } from '@/components-biz/NicConfiguration';
import type { NicPricingByDeviceType } from '@/delivery-type-plugin/components/nicPricing/utils';
import { ResourceConfigAddCard, ResourceConfigCard } from '@/delivery-type-plugin/components/resourceConfig';
import type { NicConfigurationValue } from '@/types/nic-configuration';

import { NicConfigModal } from './NicConfigModal';
import styles from './group-spec-modal.module.less';

const EMBEDDED_NICS_FIELD = 'resource.nics';
const NIC_MODAL_NICS_FIELD = 'nics';
const NIC_RESOURCE_ICON = 'icon-wangluo';

export interface NicResourceBlockProps {
  form: FormInstance;
  parentForm?: FormInstance;
  nicsField?: string;
  enabledField?: string;
  billModelId?: string;
  deploymentTypeId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  lockActionsWhenFilled?: boolean;
  presetConfigSnapshot?: Record<string, unknown>;
  nicPricingOptions?: Record<string, unknown>;
}

function resolveNicVendorDeviceSummary(nics: NicConfigurationValue | undefined, nicPricing?: NicPricingByDeviceType): string {
  if (nicPricing && Object.keys(nicPricing).length) {
    const labels = [
      ...new Set(
        Object.values(nicPricing)
          .map((meta) => formatNicHardwareSummary(meta ?? {}))
          .filter((item) => item !== '-'),
      ),
    ];
    if (labels.length) return labels.join('; ');
  }
  return buildNicVendorDeviceSummary(nics);
}

function buildNicSummaryItems(nics: NicConfigurationValue | undefined, nicPricing?: NicPricingByDeviceType) {
  const specLines = buildNicSummaryLines(nics);
  return [
    { label: '资源配置', value: specLines.join('; ') || '-' },
    { label: '厂商设备', value: resolveNicVendorDeviceSummary(nics, nicPricing) },
  ];
}

function hasNicCardData(nics: unknown, enabled?: boolean): boolean {
  if (enabled === false) return false;
  return Array.isArray(nics) && nics.length > 0;
}

/** 节点抽屉网卡能力块：卡片 + Modal（非必填） */
export function NicResourceBlock({
  form,
  parentForm,
  nicsField = EMBEDDED_NICS_FIELD,
  enabledField = '_nicEnabled',
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  presetConfigSnapshot,
  nicPricingOptions,
}: NicResourceBlockProps) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const isOrderFixed = resourceEditMode === 'order-fixed';
  const locked = isOrderFixed;

  const nics = Form.useWatch(nicsField, form);
  const nicEnabled = Form.useWatch(enabledField, form);
  const nicPricing = Form.useWatch('network.nicPricing', parentForm ?? form) as NicPricingByDeviceType | undefined;

  const hasCardData = hasNicCardData(nics, nicEnabled);
  const summaryItems = useMemo(() => buildNicSummaryItems(nics as NicConfigurationValue | undefined, nicPricing), [nicPricing, nics]);

  const modalInitialValues = useMemo(() => {
    if (!modalOpen) return undefined;
    return { [NIC_MODAL_NICS_FIELD]: nics ?? [] };
  }, [modalOpen, nics]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleModalOk = useCallback(
    (modalValues: Record<string, unknown>) => {
      const nextNics = modalValues[NIC_MODAL_NICS_FIELD];
      form.setFieldValue(nicsField, nextNics);
      form.setFieldValue(enabledField, Array.isArray(nextNics) && nextNics.length > 0);
      closeModal();
    },
    [closeModal, enabledField, form, nicsField],
  );

  const handleRemove = useCallback(() => {
    form.setFieldValue(nicsField, undefined);
    form.setFieldValue(enabledField, false);
  }, [enabledField, form, nicsField]);

  const addLabel = t('data-center:network.card');

  return (
    <>
      {hasCardData ? (
        <ResourceConfigCard
          resourceCode="Nic"
          icon={NIC_RESOURCE_ICON}
          title={addLabel}
          items={summaryItems}
          canEdit={!locked}
          canDelete={!locked}
          onEdit={openModal}
          onDelete={handleRemove}
        />
      ) : locked ? null : (
        <ResourceConfigAddCard resourceCode="Nic" icon={NIC_RESOURCE_ICON} title={addLabel} onAdd={openModal} />
      )}

      <div className={styles.persistFields} aria-hidden>
        <Form.Item field={nicsField} hidden>
          <span />
        </Form.Item>
        <Form.Item field={enabledField} hidden triggerPropName="checked">
          <Checkbox />
        </Form.Item>
      </div>

      {modalOpen ? (
        <NicConfigModal
          parentForm={parentForm}
          modalNicsField={NIC_MODAL_NICS_FIELD}
          initialValues={modalInitialValues}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          resourceEditMode={resourceEditMode}
          presetConfigSnapshot={presetConfigSnapshot}
          nicPricingOptions={nicPricingOptions}
          onOk={handleModalOk}
          onCancel={closeModal}
        />
      ) : null}
    </>
  );
}
